import { exportModeLabels, type AccountRef, type BreakdownLine, type ExportPreview } from '../exportTypes';
import { DIMENSION_LABEL, LEGACY_EXPLANATION, LEGACY_LABEL, NET_PAY_COPY, SOURCE_LABEL, describeNetPay, describeWage, isLegacyOptions } from '../exportOptions';
import { formatCents, sumCents } from '../money';

const accountText = (a: AccountRef | null): string => (a ? `${a.accountNumber}${a.accountName ? ` ${a.accountName}` : ''}`.trim() : 'No account');

/** The effective options of a preview, in words. A legacy preview is labelled "Legacy / default" and never shown as if someone had chosen it. */
export function ConfigurationBlock({ preview }: { preview: ExportPreview }) {
  const { options, configuration } = preview;
  const legacy = isLegacyOptions(options);
  return (
    <section className="ax-block" aria-labelledby="ax-config-title" data-testid="preview-configuration">
      <h4 id="ax-config-title">Export configuration</h4>
      <dl className="jr-facts jr-facts--tight">
        <div className="jr-facts__row"><dt>Journal mode</dt><dd>{exportModeLabels[preview.mode]}</dd></div>
        <div className="jr-facts__row"><dt>Net pay detail</dt><dd data-testid="config-netpay">{describeNetPay(options, preview.mode)}</dd></div>
        <div className="jr-facts__row"><dt>Wage expense mapping</dt><dd data-testid="config-wage">{describeWage(options)}</dd></div>
        <div className="jr-facts__row">
          <dt>Options come from</dt>
          <dd data-testid="config-source">{legacy ? <span className="ax-legacy-tag">{LEGACY_LABEL}</span> : (options.source ? SOURCE_LABEL[options.source] : 'Not stated')}</dd>
        </div>
        {configuration?.mappingVersionMax !== null && configuration?.mappingVersionMax !== undefined && (
          <div className="jr-facts__row"><dt>Highest mapping version</dt><dd>{configuration.mappingVersionMax}</dd></div>
        )}
        {configuration?.exportKeyVersion !== null && configuration?.exportKeyVersion !== undefined && (
          <div className="jr-facts__row"><dt>Identity key version</dt><dd>v{configuration.exportKeyVersion}</dd></div>
        )}
        {options.settingsVersion !== null && <div className="jr-facts__row"><dt>Company settings version</dt><dd>{options.settingsVersion}</dd></div>}
        {options.groupConfigVersion !== null && <div className="jr-facts__row"><dt>Group configuration version</dt><dd>{options.groupConfigVersion}</dd></div>}
      </dl>
      {legacy && <p className="jr-hint">{LEGACY_EXPLANATION}</p>}
    </section>
  );
}

export function SummaryCounts({ preview }: { preview: ExportPreview }) {
  const s = preview.summary;
  if (!s) return null;
  const cells: { label: string; value: string }[] = [
    { label: 'Employees', value: s.employeeCount === null ? '' : String(s.employeeCount) },
    { label: 'Wage groups', value: s.groupCount === null ? '' : String(s.groupCount) },
    { label: 'Earning codes', value: s.earningCodeCount === null ? '' : String(s.earningCodeCount) },
    { label: 'Deduction codes', value: s.deductionCodeCount === null ? '' : String(s.deductionCodeCount) },
    { label: 'Mapped components', value: s.mappedComponents === null ? '' : String(s.mappedComponents) },
    { label: 'Missing mappings', value: s.missingMappings === null ? '' : String(s.missingMappings) },
    { label: 'Ambiguous mappings', value: s.ambiguousMappings === null ? '' : String(s.ambiguousMappings) },
    ...(s.totalDebitsCents !== null ? [{ label: 'Total debits', value: formatCents(s.totalDebitsCents) }] : []),
    ...(s.totalCreditsCents !== null ? [{ label: 'Total credits', value: formatCents(s.totalCreditsCents) }] : []),
    ...(s.varianceCents !== null ? [{ label: 'Variance', value: formatCents(s.varianceCents) }] : []),
  ].filter((c) => c.value !== '');
  return (
    <dl className="ax-counts" aria-label="Summary counts" data-testid="preview-counts">
      {cells.map((c) => <div key={c.label}><dt>{c.label}</dt><dd>{c.value}</dd></div>)}
    </dl>
  );
}

function AmountTable({ caption, lines, testId }: { caption: string; lines: BreakdownLine[]; testId: string }) {
  return (
    <div className="jr-table-scroll" role="region" aria-label={caption} tabIndex={0}>
      <table className="jr-table" data-testid={testId}>
        <caption className="ax-caption">{caption}</caption>
        <thead><tr><th scope="col">Item</th><th scope="col" className="num">Amount</th><th scope="col">GL account</th></tr></thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.key}><td>{l.label}{l.note && <span className="jr-hint ax-note"> {l.note}</span>}</td><td className="num">{formatCents(l.amountCents)}</td><td>{accountText(l.account)}</td></tr>
          ))}
        </tbody>
        <tfoot><tr><th scope="row">Total</th><td className="num">{formatCents(sumCents(lines.map((l) => l.amountCents)))}</td><td /></tr></tfoot>
      </table>
    </div>
  );
}

/** The expandable breakdowns behind the journal: wage groups with their account and the reason, taxes by type, deductions by category and employer contributions. */
export function BreakdownSections({ preview }: { preview: ExportPreview }) {
  const { wageGroups, taxesByType, deductionsByCategory, employerContributions, unusedWageMappings } = preview;
  const dimension = preview.options.wageGroupDimension;
  const grouped = preview.options.wageMappingMode === 'GroupBased';
  if (wageGroups.length === 0 && taxesByType.length === 0 && deductionsByCategory.length === 0 && !employerContributions && !unusedWageMappings) return null;
  return (
    <div data-testid="preview-breakdown">
      {wageGroups.length > 0 && (
        <details className="ax-breakdown" data-testid="breakdown-wage-groups">
          <summary>{grouped ? `Wage expense by ${dimension ? DIMENSION_LABEL[dimension].toLowerCase() : 'group'} (${wageGroups.length})` : `Wage expense (${wageGroups.length})`}</summary>
          <div className="jr-table-scroll" role="region" aria-label="Wage expense by group" tabIndex={0}>
            <table className="jr-table">
              <caption className="ax-caption">Gross wages by group, with the account each posts to and why</caption>
              <thead>
                <tr><th scope="col">Group</th><th scope="col" className="num">Employees</th><th scope="col" className="num">Gross wages</th><th scope="col">GL account</th><th scope="col">Why this account</th></tr>
              </thead>
              <tbody>
                {wageGroups.map((g) => (
                  <tr key={`${g.groupKey}-${g.label}`}>
                    <th scope="row">{g.label}</th>
                    <td className="num">{g.employeeCount}</td>
                    <td className="num">{formatCents(g.grossAmountCents)}</td>
                    <td>{accountText(g.account)}</td>
                    <td>{g.reason ?? ''}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><th scope="row">Total</th><td className="num">{wageGroups.reduce((n, g) => n + g.employeeCount, 0)}</td><td className="num">{formatCents(sumCents(wageGroups.map((g) => g.grossAmountCents)))}</td><td colSpan={2} /></tr></tfoot>
            </table>
          </div>
          {unusedWageMappings && unusedWageMappings.count > 0 && (
            <p className="jr-hint" data-testid="unused-wage-mappings">
              {unusedWageMappings.count} wage mapping{unusedWageMappings.count === 1 ? ' is' : 's are'} not used by this mode. {unusedWageMappings.note ?? ''}
            </p>
          )}
        </details>
      )}
      {taxesByType.length > 0 && (
        <details className="ax-breakdown" data-testid="breakdown-taxes">
          <summary>Taxes by type ({taxesByType.length})</summary>
          <AmountTable caption="Taxes by type" lines={taxesByType} testId="taxes-table" />
        </details>
      )}
      {deductionsByCategory.length > 0 && (
        <details className="ax-breakdown" data-testid="breakdown-deductions">
          <summary>Deductions by category ({deductionsByCategory.length})</summary>
          <AmountTable caption="Deductions by category" lines={deductionsByCategory} testId="deductions-table" />
          <p className="jr-hint">The payroll supplies three deduction totals, not amounts per code, so the journal posts these totals under their category accounts.</p>
        </details>
      )}
      {employerContributions && (
        <details className="ax-breakdown" data-testid="breakdown-employer">
          <summary>Employer contributions</summary>
          {employerContributions.notSupplied
            ? <p data-testid="employer-not-supplied"><strong>Not supplied by payroll.</strong> The payroll calculation does not supply employer benefit or retirement contributions, so none are posted and none are shown as zero.</p>
            : <AmountTable caption="Employer contributions" lines={employerContributions.items} testId="employer-table" />}
        </details>
      )}
    </div>
  );
}

/** The consolidated net pay line adds up from these employees. It is shown only when the server sent it (the caller may view per-employee detail) and it is never written to the CSV. */
export function NetPayReconciliation({ preview }: { preview: ExportPreview }) {
  const detail = preview.netPayDetail;
  if (!detail) return null;
  const total = sumCents(detail.map((d) => d.netPayCents));
  const line = sumCents((preview.journal?.lines ?? []).filter((l) => l.category === 'NetPayClearing').map((l) => (l.side === 'Credit' ? l.amountCents : -l.amountCents)));
  return (
    <details className="ax-breakdown" data-testid="netpay-reconciliation">
      <summary>Net pay by employee: reconciliation only, not exported ({detail.length})</summary>
      <p className="jr-hint">{NET_PAY_COPY.Consolidated} This list is not written to the CSV; it only shows what the single net pay line adds up to.</p>
      <div className="jr-table-scroll" role="region" aria-label="Net pay by employee" tabIndex={0}>
        <table className="jr-table">
          <caption className="ax-caption">Net pay of each employee behind the consolidated line (employee numbers only)</caption>
          <thead><tr><th scope="col">Employee</th><th scope="col" className="num">Net pay</th></tr></thead>
          <tbody>{detail.map((d) => <tr key={d.employeeReference}><td>{d.employeeReference}</td><td className="num">{formatCents(d.netPayCents)}</td></tr>)}</tbody>
          <tfoot>
            <tr><th scope="row">Total</th><td className="num" data-testid="netpay-detail-total">{formatCents(total)}</td></tr>
          </tfoot>
        </table>
      </div>
      <p className={total === line ? 'jr-ok' : 'jr-error'} data-testid="netpay-detail-match">
        {total === line ? <><span aria-hidden="true">✔ </span>Matches the net pay line in the journal ({formatCents(line)}).</> : <><span aria-hidden="true">✖ </span>Does not match the net pay line in the journal ({formatCents(line)}). Do not export.</>}
      </p>
    </details>
  );
}

export function LimitationsList({ preview }: { preview: ExportPreview }) {
  if (preview.limitations.length === 0) return null;
  return (
    <details className="ax-breakdown" data-testid="preview-limitations">
      <summary>Limitations of this export ({preview.limitations.length})</summary>
      <ul className="jr-plain-list">{preview.limitations.map((l) => <li key={l}>{l}</li>)}</ul>
    </details>
  );
}
